import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ContentFile } from "./ContentFile";
import { ContentBlock } from "./ContentBlock";
import { QuizSection } from "./QuizSection";
import { Module, Section, Question } from "../../schemas/schemas";
import type { SetViewerOption } from "../../schemas/book-schemas";
import { StudentHiddenBadge } from "./StudentHiddenBadge";
import { ReaderVisibleToggle } from "./ReaderVisibleToggle";
import { isVisibleToReaders, type SetReaderVisible } from "../../schemas/book-schemas";
import toast from "react-hot-toast";

interface BookSection {
  id: string; // MongoDB ObjectId
  title: string;
  order: number;
  duration?: number;
  type: string;
  start_day_offset?: number;
  start_day_offset_relative_to?: "book" | "chapter" | "section" | "module";
  start_day_offset_reference_id?: string;
  file_id?: string;
  file?: {
    file_id: string; // Changed from 'id' to 'file_id' to match book-composer
    file_name: string;
    file_type: string; // 'scorm', 'pdf', 'video', 'epub', 'url' for external SCORM URLs
    file_size: number;
    file_url: string;
    file_path?: string;
  };
  content?: {
    file?: {
      name: string;
      type: string;
      size: number;
      url: string;
      id: string;
    };
  };
  content_block?: {
    content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'text' | 'list' | 'flashcard';
    payload: any;
  };
  content_blocks?: Array<{
    content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'text' | 'list' | 'flashcard';
    payload: any;
    appearance?: {
      background?: {
        color: string;
        image?: string | null;
        fit?: 'cover' | 'contain' | 'fill';
        opacity?: number;
      };
      padding?: {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
      };
    };
    layout?: {
      mode: 'flow' | 'fixed' | 'grid';
    };
    timing?: {
      startMs?: number;
      endMs?: number | null;
      autoplay?: boolean;
    };
  }>;
  visible_to_readers?: boolean;
  access?: {
    visibility: 'public' | 'registered' | 'paid';
    is_free_preview: boolean;
    unlock_conditions: string[];
  };
  progress_rules?: {
    completion_type: 'viewed' | 'scorm_report' | 'quiz_pass';
    passing_score?: number;
  };
  estimates?: {
    reading_time_min: number;
  };
  children?: BookSection[]; // For nested sections in container sections
}

interface BookModule {
  id: string; // MongoDB ObjectId
  title: string;
  type: "e-learning" | "live-online" | "in-person" | "offline" | "combined" | "file" | "practical";
  duration: number;
  start_day_offset: number;
  start_day_offset_relative_to: "course" | "module" | "section";
  start_day_offset_reference_id?: string;
  order: number;
  visible_to_readers?: boolean;
  sections: BookSection[];
}

interface ModuleCardProps {
  module: Module;
  bookModule: BookModule; // Add original book module data
  moduleIndex: number;
  onContentUpload?: (sessionId: string, file: File, contentType: string, fileData?: any, blockIndex?: number) => void;
  onContentDelete?: (sessionId: string, contentId: string) => void;
  onAddSection?: () => void;
  onAddSubSection?: (parentSectionId: string) => void; // Add support for adding subsections
  isAddingSection?: boolean;
  isAddingSubSection?: string | null; // Track which section is adding subsections
  onUpdateModule?: (updatedModule: Partial<Module>) => void;
  onUpdateSection?: (sectionId: string, updatedSection: Partial<Section>) => void;
  onDeleteModule?: () => void;
  onDeleteSection?: (sectionId: string) => void;
  onAddQuestion?: (sectionId: string) => void;
  onEditQuestion?: (sectionId: string, question: any) => void;
  onSaveQuestion?: (sectionId: string, question: Question) => void;
  onDeleteQuestion?: (sectionId: string, questionId: string) => void;
  onUpdateQuizSettings?: (sectionId: string, settings: any) => void;
  /** Per-block viewer switches (allow_print / allow_download on PDFs, visible_to_students on any block): writes payload.viewer[option] */
  onSetViewerOption?: SetViewerOption;
  /** Section-level switch (chapter or lesson): writes section.visible_to_readers */
  onSetReaderVisible?: SetReaderVisible;
  t: (key: string) => string;
}

export function ModuleCard({
  module,
  bookModule, // Add original book module data
  moduleIndex,
  onContentUpload,
  onContentDelete,
  onAddSection,
  onAddSubSection,
  isAddingSection,
  isAddingSubSection,
  onUpdateModule,
  onUpdateSection,
  onDeleteModule,
  onDeleteSection,
  onAddQuestion,
  onEditQuestion,
  onSaveQuestion,
  onDeleteQuestion,
  onUpdateQuizSettings,
  onSetViewerOption,
  onSetReaderVisible,
  t,
}: ModuleCardProps) {
  
  // State for editing titles
  const [isEditingChapterTitle, setIsEditingChapterTitle] = useState(false);
  
  // State for inline question editing
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editingQuestionSectionId, setEditingQuestionSectionId] = useState<string | null>(null);
  const [isEditingExistingQuestion, setIsEditingExistingQuestion] = useState(false);
  const [editingChapterTitle, setEditingChapterTitle] = useState(module.title);
  const [editingSectionTitle, setEditingSectionTitle] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  // Update editing title when module title changes
  useEffect(() => {
    setEditingChapterTitle(module.title);
  }, [module.title]);


  // Handler for starting inline question editing
  const handleStartEditingQuestion = (sectionId: string, questionType: string) => {
    const newQuestion: Question = {
      question_id: `temp_${Date.now()}`,
      question_text: '',
      question_type: questionType as any,
      options: questionType === 'multiple_choice' ? ['', ''] : [],
      correct_answer: '',
    };
    setEditingQuestion(newQuestion);
    setEditingQuestionSectionId(sectionId);
    setIsEditingExistingQuestion(false);
  };

  // Handler for editing existing question inline
  const handleEditQuestionInline = (sectionId: string, question: Question) => {
    setEditingQuestion(question);
    setEditingQuestionSectionId(sectionId);
    setIsEditingExistingQuestion(true);
  };

  // Handler for saving inline question
  const handleSaveInlineQuestion = (question: Question) => {
    if (editingQuestionSectionId && onSaveQuestion) {
      onSaveQuestion(editingQuestionSectionId, question);
    }
    setEditingQuestion(null);
    setEditingQuestionSectionId(null);
    setIsEditingExistingQuestion(false);
  };

  // Handler for canceling inline question editing
  const handleCancelInlineQuestion = () => {
    setEditingQuestion(null);
    setEditingQuestionSectionId(null);
    setIsEditingExistingQuestion(false);
  };

  // Handler for deleting inline question
  const handleDeleteInlineQuestion = (questionId: string) => {
    if (editingQuestionSectionId && onDeleteQuestion) {
      onDeleteQuestion(editingQuestionSectionId, questionId);
    }
    setEditingQuestion(null);
    setEditingQuestionSectionId(null);
    setIsEditingExistingQuestion(false);
  };

  // Handler for starting chapter title edit
  const handleStartEditChapterTitle = () => {
    setEditingChapterTitle(module.title);
    setIsEditingChapterTitle(true);
  };

  // Handler for saving chapter title
  const handleSaveChapterTitle = () => {
    if (editingChapterTitle.trim() && onUpdateModule) {
      onUpdateModule({ title: editingChapterTitle.trim() });
    }
    setIsEditingChapterTitle(false);
  };

  // Handler for canceling chapter title edit
  const handleCancelChapterTitleEdit = () => {
    setEditingChapterTitle(module.title);
    setIsEditingChapterTitle(false);
  };

  // Handler for starting section title edit
  const handleStartEditSectionTitle = (sectionId: string, currentTitle: string) => {
    setEditingSectionId(sectionId);
    setEditingSectionTitle(currentTitle);
  };

  // Handler for saving section title
  const handleSaveSectionTitle = async () => {
    if (editingSectionId && editingSectionTitle?.trim() && onUpdateSection) {
      try {
        await onUpdateSection(editingSectionId, { title: editingSectionTitle.trim() });
        // Only clear editing state after successful update
        setEditingSectionId(null);
        setEditingSectionTitle(null);
      } catch (error) {
        console.error("Failed to save section title:", error);
        // Keep editing state if update failed
      }
    } else {
      // Clear editing state if no valid data
      setEditingSectionId(null);
      setEditingSectionTitle(null);
    }
  };

  // Handler for canceling section title edit
  const handleCancelSectionTitleEdit = () => {
    setEditingSectionId(null);
    setEditingSectionTitle(null);
  };

  // Helper function to get delivery method styling
  const getDeliveryMethodStyle = (method: string) => {
    switch (method) {
      case "combined":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "e-learning":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "live-online":
        return "bg-green-100 text-green-800 border-green-200";
      case "in-person":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "offline":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Helper function to get delivery method icon
  const getDeliveryMethodIcon = (method: string) => {
    switch (method) {
      case "combined":
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        );
      case "e-learning":
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
      case "live-online":
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        );
      case "in-person":
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        );
      case "offline":
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  // Helper function to translate delivery methods to Vietnamese
  const getDeliveryMethodLabel = (method: string) => {
    switch (method) {
      case "combined":
        return "Học tập tổng hợp";
      case "e-learning":
        return "Học trực tuyến";
      case "live-online":
        return "Học trực tiếp online";
      case "in-person":
        return "Học trực tiếp";
      case "offline":
        return "Học tại lớp";
      default:
        return method;
    }
  };

  // Helper function to get content type styling
  const getContentTypeStyle = (contentType: string) => {
    switch (contentType) {
      case "html":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "text":
        return "bg-cyan-100 text-cyan-800 border-cyan-200";
      case "pdf":
        return "bg-red-100 text-red-800 border-red-200";
      case "scorm":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "html_package":
        return "bg-sky-100 text-sky-800 border-sky-200";
      case "pptx":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "video":
        return "bg-green-100 text-green-800 border-green-200";
      case "audio":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "image":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "list":
        return "bg-slate-100 text-slate-800 border-slate-200";
      case "quiz":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "media":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "embed":
        return "bg-pink-100 text-pink-800 border-pink-200";
      case "merged":
        return "bg-violet-100 text-violet-800 border-violet-200";
      case "container":
        return "bg-teal-100 text-teal-800 border-teal-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Helper function to get content type label
  const getContentTypeLabel = (contentType: string) => {
    switch (contentType) {
      case "html":
        return "HTML";
      case "text":
        return "Văn bản";
      case "pdf":
        return "PDF";
      case "scorm":
        return "SCORM";
      case "html_package":
        return "Học liệu HTML";
      case "pptx":
        return "Slide PowerPoint";
      case "video":
        return "Video";
      case "audio":
        return "Audio";
      case "image":
        return "Hình ảnh";
      case "list":
        return "Danh sách";
      case "quiz":
        return "Bài kiểm tra";
      case "media":
        return "Đa phương tiện";
      case "embed":
        return "Nội dung nhúng";
      case "container":
        return "Phần chứa";
      default:
        return contentType;
    }
  };


  return (
    <div key={module.id} className="space-y-6">
      <Card>
        <CardHeader className="bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {isEditingChapterTitle ? (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="text"
                      value={editingChapterTitle}
                      onChange={(e) => setEditingChapterTitle(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveChapterTitle();
                        } else if (e.key === 'Escape') {
                          handleCancelChapterTitleEdit();
                        }
                      }}
                    />
                    <button
                      onClick={handleSaveChapterTitle}
                      className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                    >
                      ✓
                    </button>
                    <button
                      onClick={handleCancelChapterTitleEdit}
                      className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <span 
                    className="group cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={handleStartEditChapterTitle}
                    title="Click để chỉnh sửa"
                  >
                    {module.title}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-2 opacity-0 group-hover:opacity-100 inline-block"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </span>
                )}
              </CardTitle>
              <div className="text-sm text-muted-foreground flex items-center">
                <span className="mr-2">Loại:</span>
                <div
                  className={`flex items-center text-xs font-medium px-2 py-1 rounded-md border ${getDeliveryMethodStyle(
                    module.type
                  )}`}
                >
                  {getDeliveryMethodIcon(module.type)}
                  {getDeliveryMethodLabel(module.type)}
                </div>
                <span className="ml-2 text-gray-500">| {(module.sections || []).length} phần</span>
                {/* `module` là adapter đã lược, sections không mang khối nội dung;
                    `bookModule` mới là cây thật */}
                <StudentHiddenBadge section={bookModule} className="ml-2" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ReaderVisibleToggle
                section={bookModule}
                noun="chương"
                onSetReaderVisible={onSetReaderVisible}
              />
              {onAddSection && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAddSection()}
                    disabled={isAddingSection}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    {isAddingSection ? "Đang thêm..." : "Thêm phần"}
                  </Button>
                </>
              )}
              {onDeleteModule && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (module.id) {
                      onDeleteModule();
                    } else {
                      toast.error("Không thể xóa chương: ID không hợp lệ!");
                    }
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 ps-0 pe-0 pl-0 pr-0">
          <div className="p-0">
            {module.type === "content" && (
              <>
                <ContentFile
                  key={module.id}
                  sessionId={module.id}
                  sessionName={module.title}
                  onContentUpload={onContentUpload}
                  onContentDelete={onContentDelete}
                  onSetViewerOption={onSetViewerOption}
                  currentFile={
                    module.content_block?.payload?.file
                      ? {
                          file_id: module.content_block.payload.file.id,
                          file_name: module.content_block.payload.file.name,
                          file_type: module.content_block.payload.file.type,
                          file_size: module.content_block.payload.file.size,
                          file_url: module.content_block.payload.file.url,
                          file_path: module.content_block.payload.file.file_path,
                        }
                      : module.content_block?.payload?.file
                  }
                  contentBlock={module.content_block}
                />
              </>
            )}
            {/* Activities */}
            <div className="space-y-2">
              {(bookModule.sections || []).map((section: BookSection, sectionIndex: number) => {
                return (
                  <div key={section.id} className="overflow-hidden" data-section-id={section.id}>
                    {/* Section Header: phần đã tắt nền xám, tên gạch ngang để nhìn lướt là biết người đọc không thấy */}
                    <div
                      className={`py-2 px-4 border-b border-gray-200 dark:border-gray-700 ${
                        isVisibleToReaders(section)
                          ? "bg-blue-50 dark:bg-blue-900/20"
                          : "bg-gray-100 dark:bg-gray-800"
                      }`}
                    >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="mr-2 text-blue-600">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M21 15V10a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"></path>
                                <path d="M17 10h4"></path>
                                <path d="M19 13h2"></path>
                              </svg>
                            </span>
                            <div>
                              <div className="font-medium text-sm">
                                {editingSectionId === section.id ? (
                                  <div className="flex items-center gap-2 mt-1">
                                    <input
                                      type="text"
                                      value={editingSectionTitle || ''}
                                      onChange={(e) => setEditingSectionTitle(e.target.value)}
                                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleSaveSectionTitle();
                                        } else if (e.key === 'Escape') {
                                          handleCancelSectionTitleEdit();
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={handleSaveSectionTitle}
                                      className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={handleCancelSectionTitleEdit}
                                      className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <span 
                                    className={`group cursor-pointer hover:text-blue-600 transition-colors ${
                                      isVisibleToReaders(section) ? "" : "line-through text-gray-400"
                                    }`}
                                    onClick={() => handleStartEditSectionTitle(section.id, section.title)}
                                    title="Click để chỉnh sửa"
                                  >
                                    {section.title}
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="ml-2 opacity-0 group-hover:opacity-100 inline-block"
                                    >
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <StudentHiddenBadge section={section} />
                                {/* Content Type Badge */}
                                {(section.content_block || section.content_blocks) && (
                                  <div
                                    className={`flex items-center text-xs font-medium px-2 py-1 rounded-md border ${getContentTypeStyle(
                                      section.content_blocks 
                                        ? 'merged' 
                                        : section.type === 'container' 
                                          ? 'container' 
                                          : section.content_block?.content_type || 'content'
                                    )}`}
                                  >
                                    {section.content_blocks
                                      ? `Nội dung gộp (${section.content_blocks.length} khối)`
                                      : getContentTypeLabel(section.type === 'container' ? 'container' : section.content_block?.content_type || 'content')}
                                  </div>
                                )}


                                {/* Duration */}
                                {section.duration && section.duration > 0 && (
                                  <span className="text-xs text-gray-500">
                                    {section.duration} phút
                                  </span>
                                )}

                                {/* Reading Time */}
                                {section.estimates?.reading_time_min && (
                                  <span className="text-xs text-gray-500">
                                    ~{section.estimates.reading_time_min} phút đọc
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <ReaderVisibleToggle
                              section={section}
                              noun="phần"
                              onSetReaderVisible={onSetReaderVisible}
                            />
                            {/* Add Subsection button for container sections */}
                            {section.type === 'container' && onAddSubSection && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onAddSubSection(section.id)}
                                disabled={isAddingSubSection === section.id}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <line x1="12" y1="5" x2="12" y2="19"></line>
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                {isAddingSubSection === section.id ? "Đang thêm..." : "Thêm phần con"}
                              </Button>
                            )}
                            {/* Delete button - always visible for any section */}
                            {onDeleteSection && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (section.id) {
                                    onDeleteSection(section.id);
                                  } else {
                                    toast.error("Không thể xóa phần: ID không hợp lệ!");
                                  }
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                              </Button>
                            )}
                          </div>
                        </div>
                    </div>

                    {/* Render content_blocks array (merged blocks) - Priority over single content_block */}
                    {section.content_blocks && Array.isArray(section.content_blocks) && section.content_blocks.length > 0 ? (
                      <div className="space-y-0">
                        {section.content_blocks.map((block, blockIndex) => (
                          <ContentBlock
                            key={blockIndex}
                            block={block}
                            sessionId={section.id}
                            sessionName={section.title}
                            blockIndex={blockIndex}
                            onContentUpload={onContentUpload}
                            onContentDelete={onContentDelete}
                            onSetViewerOption={onSetViewerOption}
                            size="default"
                          />
                        ))}
                      </div>
                    ) : (
                      <>
                        {/* Single content_block rendering */}
                        {section.content_block && section.type !== 'container' && (
                          <div className="p-4">
                            <ContentBlock
                              block={section.content_block}
                              sessionId={section.id}
                              sessionName={section.title}
                              onContentUpload={onContentUpload}
                              onContentDelete={onContentDelete}
                              onSetViewerOption={onSetViewerOption}
                              size="default"
                            />
                          </div>
                        )}

                        {/* Fallback to ContentFile if no content_block but has file info */}
                        {!section.content_block && 
                         section.type !== 'container' && 
                         !section.content_blocks &&
                         (section.content?.file || section.file) && (
                          <div className="p-0">
                            <ContentFile
                              key={section.id}
                              sessionId={section.id}
                              sessionName={section.title}
                              onContentUpload={onContentUpload}
                              onContentDelete={onContentDelete}
                              onSetViewerOption={onSetViewerOption}
                              currentFile={
                                section.content?.file
                                  ? {
                                      file_id: section.content.file.id,
                                      file_name: section.content.file.name,
                                      file_type: section.content.file.type,
                                      file_size: section.content.file.size,
                                      file_url: section.content.file.url,
                                    }
                                  : section.file
                              }
                              contentBlock={section.content_block}
                            />
                          </div>
                        )}
                      </>
                    )}
                      
                      <div className="p-0">
                      {/* Quiz Questions Section */}
                      <QuizSection
                        section={section}
                        onUpdateQuizSettings={onUpdateQuizSettings}
                        onStartEditingQuestion={handleStartEditingQuestion}
                        onEditQuestionInline={handleEditQuestionInline}
                        onDeleteQuestion={onDeleteQuestion}
                        editingQuestion={editingQuestion}
                        editingQuestionSectionId={editingQuestionSectionId}
                        isEditingExistingQuestion={isEditingExistingQuestion}
                        onSaveInlineQuestion={handleSaveInlineQuestion}
                        onCancelInlineQuestion={handleCancelInlineQuestion}
                        onDeleteInlineQuestion={handleDeleteInlineQuestion}
                      />
                      
                      {/* Subsections for Container Sections */}
                      {section.type === 'container' && section.children && section.children.length > 0 && (
                        <div className="ml-6 mt-2 space-y-2 border-l-2 border-gray-200 pl-4">
                          <div className="text-sm font-medium text-gray-600 mb-2">
                            Phần con ({section.children.length})
                          </div>
                          {section.children.map((subSection: BookSection, subIndex: number) => (
                            <div key={subSection.id} className="bg-gray-50 rounded-lg overflow-hidden" data-section-id={subSection.id}>
                              {/* Subsection Header */}
                              <div className="py-2 px-3 bg-gray-100 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <span className="mr-2 text-gray-600">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14,2 14,8 20,8"></polyline>
                                      </svg>
                                    </span>
                                    <div>
                                      <div className="font-medium text-sm">
                                        {editingSectionId === subSection.id ? (
                                          <div className="flex items-center gap-2 mt-1">
                                            <input
                                              type="text"
                                              value={editingSectionTitle || ''}
                                              onChange={(e) => setEditingSectionTitle(e.target.value)}
                                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                              autoFocus
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  handleSaveSectionTitle();
                                                } else if (e.key === 'Escape') {
                                                  handleCancelSectionTitleEdit();
                                                }
                                              }}
                                            />
                                            <button
                                              onClick={handleSaveSectionTitle}
                                              className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                            >
                                              ✓
                                            </button>
                                            <button
                                              onClick={handleCancelSectionTitleEdit}
                                              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        ) : (
                                          <span 
                                            className="group cursor-pointer hover:text-blue-600 transition-colors"
                                            onClick={() => handleStartEditSectionTitle(subSection.id, subSection.title)}
                                            title="Click để chỉnh sửa"
                                          >
                                            {subSection.title}
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="12"
                                              height="12"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="ml-2 opacity-0 group-hover:opacity-100 inline-block"
                                            >
                                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        {(subSection.content_block || subSection.content_blocks) && (
                                          <div
                                            className={`flex items-center text-xs font-medium px-2 py-1 rounded-md border ${getContentTypeStyle(
                                              subSection.content_blocks
                                                ? 'merged'
                                                : subSection.type === 'container' 
                                                  ? 'container' 
                                                  : subSection.content_block?.content_type || 'content'
                                            )}`}
                                          >
                                            {subSection.content_blocks
                                              ? `Nội dung gộp (${subSection.content_blocks.length} khối)`
                                              : getContentTypeLabel(subSection.type === 'container' ? 'container' : subSection.content_block?.content_type || 'content')}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {/* Delete button for subsections */}
                                    {onDeleteSection && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          if (subSection.id) {
                                            onDeleteSection(subSection.id);
                                          } else {
                                            toast.error("Không thể xóa phần con: ID không hợp lệ!");
                                          }
                                        }}
                                        className="text-red-600 hover:text-red-700 bg-red-50 border border-red-200 min-h-[28px] min-w-[28px] p-1 flex items-center justify-center rounded"
                                        title="Xóa phần con"
                                        data-testid="delete-subsection-button"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="12"
                                          height="12"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="3 6 5 6 21 6"></polyline>
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        </svg>
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Subsection Content */}
                              <div className="p-2">
                                {/* Render content_blocks array (merged blocks) for subsections - Priority */}
                                {subSection.content_blocks && Array.isArray(subSection.content_blocks) && subSection.content_blocks.length > 0 ? (
                                  <div className="space-y-0">
                                    {subSection.content_blocks.map((block, blockIndex) => (
                                      <ContentBlock
                                        key={blockIndex}
                                        block={block}
                                        sessionId={subSection.id}
                                        sessionName={subSection.title}
                                        blockIndex={blockIndex}
                                        onContentUpload={onContentUpload}
                                        onContentDelete={onContentDelete}
                                        onSetViewerOption={onSetViewerOption}
                                        size="compact"
                                      />
                                    ))}
                                  </div>
                                ) : subSection.content_block ? (
                                  <ContentBlock
                                    block={subSection.content_block}
                                    sessionId={subSection.id}
                                    sessionName={subSection.title}
                                    onContentUpload={onContentUpload}
                                    onContentDelete={onContentDelete}
                                    onSetViewerOption={onSetViewerOption}
                                    size="compact"
                                  />
                                ) : (subSection.content?.file || subSection.file) ? (
                                  <ContentFile
                                    key={subSection.id}
                                    sessionId={subSection.id}
                                    sessionName={subSection.title}
                                    onContentUpload={onContentUpload}
                                    onContentDelete={onContentDelete}
                                    onSetViewerOption={onSetViewerOption}
                                    currentFile={
                                      subSection.content?.file
                                        ? {
                                            file_id: subSection.content.file.id,
                                            file_name: subSection.content.file.name,
                                            file_type: subSection.content.file.type,
                                            file_size: subSection.content.file.size,
                                            file_url: subSection.content.file.url,
                                          }
                                        : subSection.file
                                    }
                                    contentBlock={undefined}
                                  />
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      </div>
                    </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
